import { create } from 'zustand';
import type { OutdoorProjectionDto, DiscountDto } from '../api/generated';
import { bestDiscount, daysBetweenInclusive } from '../utils/format';

const STORAGE_KEY = 'scapow_carts';
const ANON_KEY = '__anon__';

type CartsStorage = Record<string, CartCampaign[]>;

const readStorage = (): CartsStorage => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}'); }
  catch { return {}; }
};

const writeStorage = (carts: CartsStorage) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(carts));

const persistKey = (key: string, campaigns: CartCampaign[]) => {
  const all = readStorage();
  if (campaigns.length === 0) delete all[key];
  else all[key] = campaigns;
  writeStorage(all);
};

export type CartItem = {
  outdoorId: string;
  outdoor: OutdoorProjectionDto;
  startDate: string;
  endDate: string;
  productionCost?: number;
  discountPercentage?: number;
  discounts?: DiscountDto[];
};

export type CartCampaign = { id: string; items: CartItem[] };

interface CartState {
  campaigns: CartCampaign[];
  activeKey: string;
  addItem: (item: CartItem, campaignId: string) => void;
  removeItem: (outdoorId: string, campaignId: string) => void;
  removeCampaign: (campaignId: string) => void;
  clearCampaign: (campaignId: string) => void;
  clearAll: () => void;
  updateItemDates: (outdoorId: string, campaignId: string, startDate: string, endDate: string) => void;
  /** Call on login/logout/rehydrate. mergeAnonymous=true absorbs the guest cart into this user. */
  switchUser: (userId: string | null, mergeAnonymous?: boolean) => void;
}

export const useCartStore = create<CartState>((set) => ({
  campaigns: readStorage()[ANON_KEY] ?? [],
  activeKey: ANON_KEY,

  addItem: (item, campaignId) =>
    set((state) => {
      const existing = state.campaigns.find((c) => c.id === campaignId);
      let campaigns: CartCampaign[];
      if (existing) {
        const itemExists = existing.items.some((i) => i.outdoorId === item.outdoorId);
        campaigns = state.campaigns.map((c) =>
          c.id === campaignId
            ? {
                ...c,
                items: itemExists
                  ? c.items.map((i) => (i.outdoorId === item.outdoorId ? item : i))
                  : [...c.items, item],
              }
            : c
        );
      } else {
        campaigns = [...state.campaigns, { id: campaignId, items: [item] }];
      }
      persistKey(state.activeKey, campaigns);
      return { campaigns };
    }),

  removeItem: (outdoorId, campaignId) =>
    set((state) => {
      const campaigns = state.campaigns
        .map((c) =>
          c.id === campaignId
            ? { ...c, items: c.items.filter((i) => i.outdoorId !== outdoorId) }
            : c
        )
        .filter((c) => c.items.length > 0);
      persistKey(state.activeKey, campaigns);
      return { campaigns };
    }),

  removeCampaign: (campaignId) =>
    set((state) => {
      const campaigns = state.campaigns.filter((c) => c.id !== campaignId);
      persistKey(state.activeKey, campaigns);
      return { campaigns };
    }),

  clearCampaign: (campaignId) =>
    set((state) => {
      const campaigns = state.campaigns.filter((c) => c.id !== campaignId);
      persistKey(state.activeKey, campaigns);
      return { campaigns };
    }),

  clearAll: () =>
    set((state) => {
      persistKey(state.activeKey, []);
      return { campaigns: [] };
    }),

  updateItemDates: (outdoorId, campaignId, startDate, endDate) =>
    set((state) => {
      const days = Math.max(0, daysBetweenInclusive(startDate, endDate));
      const campaigns = state.campaigns.map((c) => {
        if (c.id !== campaignId) return c;
        return {
          ...c,
          items: c.items.map((i) =>
            i.outdoorId !== outdoorId
              ? i
              : { ...i, startDate, endDate, discountPercentage: bestDiscount(i.discounts, days) }
          ),
        };
      });
      persistKey(state.activeKey, campaigns);
      return { campaigns };
    }),

  switchUser: (userId, mergeAnonymous = false) => {
    const newKey = userId ?? ANON_KEY;
    const all = readStorage();
    let campaigns = [...(all[newKey] ?? [])];

    if (mergeAnonymous && userId) {
      const anonCampaigns = all[ANON_KEY] ?? [];
      for (const anonCampaign of anonCampaigns) {
        if (!campaigns.find((c) => c.id === anonCampaign.id)) {
          campaigns.push(anonCampaign);
        }
      }
      delete all[ANON_KEY];
      all[newKey] = campaigns;
      writeStorage(all);
    }

    set({ campaigns, activeKey: newKey });
  },
}));
